/**
 * All StudyUser CRUD actions
 *
 * Actions are payloads of information that send data from the application
 * (i.e. Yote server) to the store. They are the _only_ source of information
 * for the store.
 *
 * NOTE: In Yote, we try to keep actions and reducers dealing with CRUD payloads
 * in terms of 'item' or 'items'. This keeps the action payloads consistent and
 * aides various scoping issues with list management in the reducers.
 */

// import api utility
import callAPI from '../../global/utils/api'

const shouldFetchSingle = (state, id) => {
  /**
   * This is helper method to determine whether we should fetch a new single
   * user object from the server, or if a valid one already exists in the store
   *
   * NOTE: Uncomment console logs to help debugging
   */
  // console.log("shouldFetch single");
  const { byId, selected } = state.studyUser;
  if(selected.id !== id) {
    // the "selected" id changed, so we _should_ fetch
    // console.log("Y shouldFetch - true: id changed");
    return true;
  } else if(selected.isFetching) {
    // "selected" is already fetching, don't do anything
    // console.log("Y shouldFetch - false: isFetching");
    return false;
  } else if(!byId[id] && !selected.error) {
    // the id is not in the map, fetch from server
    // however, if the api returned an error, then it SHOULDN'T be in the map
    // so re-fetching it will result in an infinite loop
    // console.log("Y shouldFetch - true: not in map");
    return true;
  } else if(new Date().getTime() - selected.lastUpdated > (1000 * 60 * 5)) {
    // it's been longer than 5 minutes since the last fetch, get a new one
    // console.log("Y shouldFetch - true: older than 5 minutes");
    // also, don't automatically invalidate on server error. if server throws an error,
    // that won't change on subsequent requests and we will have an infinite loop
    return true;
  } else {
    // if "selected" is invalidated, fetch a new one, otherwise don't
    // console.log("Y shouldFetch - " + selected.didInvalidate + ": didInvalidate");
    return selected.didInvalidate;
  }
}

export const INVALIDATE_SELECTED_STUDY_USER = "INVALIDATE_SELECTED_STUDY_USER"
export function invalidateSelected() {
  return {
    type: INVALIDATE_SELECTED_STUDY_USER
  }
}

export const fetchSingleIfNeeded = (id) => (dispatch, getState) => {
  if (shouldFetchSingle(getState(), id)) {
    return dispatch(fetchSingleStudyUserById(id))
  } else {
    return dispatch(returnSingleStudyUserPromise(id)); // return promise that contains studyUser
  }
}

export const returnSingleStudyUserPromise = (id) => (dispatch, getState) => {
  /**
   * This returns the object from the map so that we can do things with it in
   * the component.
   *
   * For the "fetchIfNeeded()" functionality, we need to return a promised object
   * EVEN IF we don't need to fetch it. this is because if we have any .then()'s
   * in the components, they will fail when we don't need to fetch.
   */
  return new Promise((resolve, reject) => {
    resolve({
      type: "RETURN_SINGLE_STUDY_USER_WITHOUT_FETCHING"
      , id: id
      , item: getState().studyUser.byId[id]
      , success: true
    })
  });
}

export const REQUEST_SINGLE_STUDY_USER = "REQUEST_SINGLE_STUDY_USER";
function requestSingleStudyUser(id) {
  return {
    type: REQUEST_SINGLE_STUDY_USER
    , id
  }
}

export const RECEIVE_SINGLE_STUDY_USER = "RECEIVE_SINGLE_STUDY_USER";
function receiveSingleStudyUser(json) {
  return {
    type: RECEIVE_SINGLE_STUDY_USER
    , id: json.groupUser ? json.groupUser._id : null
    , item: json.groupUser
    , success: json.success
    , error: json.message
    , receivedAt: Date.now()
  }
}

export function fetchSingleStudyUserById(studyUserId) {
  return dispatch => {
    dispatch(requestSingleStudyUser(studyUserId))
    return callAPI(`/api/group-users/${studyUserId}`)
      .then(json => dispatch(receiveSingleStudyUser(json)))
  }
}

export const ADD_SINGLE_STUDY_USER_TO_MAP = "ADD_SINGLE_STUDY_USER_TO_MAP";
export function addSingleStudyUserToMap(item) {
  return {
    type: ADD_SINGLE_STUDY_USER_TO_MAP
    , item
  }
}

export const REQUEST_SPONSOR_INVITE = "REQUEST_SPONSOR_INVITE";
function requestSponsorInvite(studyUser) {
  return {
    type: REQUEST_SPONSOR_INVITE
    , studyUser
  }
}

export const RECEIVE_SPONSOR_INVITE = "RECEIVE_SPONSOR_INVITE";
function receiveSponsorInvite(json) {
  return {
    type: RECEIVE_SPONSOR_INVITE
    , id: json.groupUser ? json.groupUser._id : null
    , item: json.groupUser
    , success: json.success
    , error: json.message
    , receivedAt: Date.now()
  }
}

export function sendSponsorInvite(data) {
  return dispatch => {
    dispatch(requestSponsorInvite(data))
    return callAPI('/api/group-users/sponsor-invite', 'POST', data)
      .then(json => dispatch(receiveSponsorInvite(json)))
  }
}

export const REQUEST_CREATE_STUDY_USER = "REQUEST_CREATE_STUDY_USER";
function requestCreateStudyUser(studyUser) {
  return {
    type: REQUEST_CREATE_STUDY_USER
    , studyUser
  }
}

export const RECEIVE_CREATE_STUDY_USER = "RECEIVE_CREATE_STUDY_USER";
function receiveCreateStudyUser(json) {
  return {
    type: RECEIVE_CREATE_STUDY_USER
    , id: json.groupUser ? json.groupUser._id : null
    , item: json.groupUser
    , success: json.success
    , error: json.message
    , receivedAt: Date.now()
  }
}

export function sendCreateStudyUser(data) {
  return dispatch => {
    dispatch(requestCreateStudyUser(data))
    return callAPI('/api/group-users', 'POST', data)
      .then(json => dispatch(receiveCreateStudyUser(json)))
  }
}

export const REQUEST_INVITE_STUDY_USER = "REQUEST_INVITE_STUDY_USER";
function requestInviteStudyUser(studyUser) {
  return {
    type: REQUEST_INVITE_STUDY_USER
    , studyUser
  }
}

export const RECEIVE_INVITE_STUDY_USER = "RECEIVE_INVITE_STUDY_USER";
function receiveInviteStudyUser(json) {
  return {
    type: RECEIVE_INVITE_STUDY_USER
    , id: json.groupUser ? json.groupUser._id : null
    , item: json.groupUser
    , success: json.success
    , error: json.message
    , receivedAt: Date.now()
  }
}

export function sendInviteStudyUser(data) {
  return dispatch => {
    dispatch(requestInviteStudyUser(data))
    return callAPI('/api/group-users/invite', 'POST', data)
      .then(json => dispatch(receiveInviteStudyUser(json)))
  }
}

export const REQUEST_RE_INVITE_STUDY_USER = "REQUEST_RE_INVITE_STUDY_USER";
function requestReInviteStudyUser(studyUser) {
  return {
    type: REQUEST_RE_INVITE_STUDY_USER
    , studyUser
  }
}

export const RECEIVE_RE_INVITE_STUDY_USER = "RECEIVE_RE_INVITE_STUDY_USER";
function receiveReInviteStudyUser(json) {
  return {
    type: RECEIVE_RE_INVITE_STUDY_USER
    , id: json.groupUser ? json.groupUser._id : null
    , item: json.groupUser
    , success: json.success
    , error: json.message
    , receivedAt: Date.now()
  }
}

export function sendReInviteStudyUser(data) {
  return dispatch => {
    dispatch(requestReInviteStudyUser(data))
    return callAPI('/api/group-users/resend-invite', 'POST', data)
      .then(json => dispatch(receiveReInviteStudyUser(json)))
  }
}

export const REQUEST_BULK_INVITE_STUDY_USERS = "REQUEST_BULK_INVITE_STUDY_USERS";
function requestBulkInviteStudyUsers(studyUsers) {
  return {
    type: REQUEST_BULK_INVITE_STUDY_USERS
    , studyUsers
  }
}

export const RECEIVE_BULK_INVITE_STUDY_USERS = "RECEIVE_BULK_INVITE_STUDY_USERS";
function receiveBulkInviteStudyUsers(json) {
  return {
    type: RECEIVE_BULK_INVITE_STUDY_USERS
    , id: json.groupUser ? json.groupUser._id : null
    , item: json.groupUser
    , success: json.success
    , error: json.message
    , receivedAt: Date.now()
  }
}

export function sendBulkInviteStudyUsers(data) {
  return dispatch => {
    dispatch(requestBulkInviteStudyUsers(data))
    return callAPI('/api/group-users/bulk-invite', 'POST', data)
      .then(json => dispatch(receiveBulkInviteStudyUsers(json)))
  }
}

export const REQUEST_BULK_DEACTIVATE_VIA_SITE = "REQUEST_BULK_DEACTIVATE_VIA_SITE";
function requestBulkDeactivateViaSite(siteId) {
  return {
    type: REQUEST_BULK_DEACTIVATE_VIA_SITE
  }
}

export const RECEIVE_BULK_DEACTIVATE_VIA_SITE = "RECEIVE_BULK_DEACTIVATE_VIA_SITE";
function receiveBulkDeactivateViaSite(json) {
  return {
    type: RECEIVE_BULK_DEACTIVATE_VIA_SITE
    , success: json.success
    , error: json.message
    , receivedAt: Date.now()
  }
}

export function sendBulkDeactivateViaSite(siteId) {
  return dispatch => {
    dispatch(requestBulkDeactivateViaSite(siteId))
    return callAPI(`/api/group-users/deactivate-by-site/${siteId}`, 'PUT', {})
      .then(json => dispatch(receiveBulkDeactivateViaSite(json)))
  }
}

export const REQUEST_BULK_DEACTIVATE_VIA_USER = "REQUEST_BULK_DEACTIVATE_VIA_USER";
function requestBulkDeactivateViaUser(userId) {
  return {
    type: REQUEST_BULK_DEACTIVATE_VIA_USER
  }
}

export const RECEIVE_BULK_DEACTIVATE_VIA_USER = "RECEIVE_BULK_DEACTIVATE_VIA_USER";
function receiveBulkDeactivateViaUser(json) {
  return {
    type: RECEIVE_BULK_DEACTIVATE_VIA_USER
    , success: json.success
    , error: json.message
    , receivedAt: Date.now()
  }
}

export function sendBulkDeactivateViaUser(userId) {
  return dispatch => {
    dispatch(requestBulkDeactivateViaUser(userId))
    return callAPI(`/api/group-users/deactivate-by-user/${userId}`, 'PUT', {})
      .then(json => dispatch(receiveBulkDeactivateViaUser(json)))
  }
}

export const REQUEST_BULK_DEACTIVATE_VIA_STUDY = "REQUEST_BULK_DEACTIVATE_VIA_STUDY";
function requestBulkDeactivateViaStudy(studyId) {
  return {
    type: REQUEST_BULK_DEACTIVATE_VIA_STUDY
  }
}

export const RECEIVE_BULK_DEACTIVATE_VIA_STUDY = "RECEIVE_BULK_DEACTIVATE_VIA_STUDY";
function receiveBulkDeactivateViaStudy(json) {
  return {
    type: RECEIVE_BULK_DEACTIVATE_VIA_STUDY
    , success: json.success
    , error: json.message
    , receivedAt: Date.now()
  }
}

export function sendBulkDeactivateViaStudy(studyId) {
  return dispatch => {
    dispatch(requestBulkDeactivateViaStudy(studyId))
    return callAPI(`/api/group-users/deactivate-by-study/${studyId}`, 'PUT', {})
      .then(json => dispatch(receiveBulkDeactivateViaStudy(json)))
  }
}

export const REQUEST_UPDATE_STUDY_USER = "REQUEST_UPDATE_STUDY_USER";
function requestUpdateStudyUser(studyUser) {
  return {
    type: REQUEST_UPDATE_STUDY_USER
    , studyUser
  }
}

export const RECEIVE_UPDATE_STUDY_USER = "RECEIVE_UPDATE_STUDY_USER";
function receiveUpdateStudyUser(json) {
  return {
    type: RECEIVE_UPDATE_STUDY_USER
    , item: json.groupUser
    , success: json.success
    , error: json.message
    , receivedAt: Date.now()
  }
}

export function sendUpdateStudyUser(data) {
  return dispatch => {
    dispatch(requestUpdateStudyUser(data))
    return callAPI(`/api/group-users/${data._id}`, 'PUT', data)
      .then(json => dispatch(receiveUpdateStudyUser(json)))
  }
}

export const REQUEST_DELETE_STUDY_USER = "REQUEST_DELETE_STUDY_USER";
function requestDeleteStudyUser(studyUserId) {
  return {
    type: REQUEST_DELETE_STUDY_USER
    , studyUserId
  }
}

export const RECEIVE_DELETE_STUDY_USER = "RECEIVE_DELETE_STUDY_USER";
function receiveDeleteStudyUser(json) {
  return {
    type: RECEIVE_DELETE_STUDY_USER
    , success: json.success
    , error: json.message
    , receivedAt: Date.now()
  }
}

export function sendDelete(id) {
  return dispatch => {
    dispatch(requestDeleteStudyUser(id))
    return callAPI(`/api/group-users/${id}`, 'DELETE')
      .then(json => dispatch(receiveDeleteStudyUser(json)))
  }
}

/**
 * STUDY_USER LIST ACTIONS
 */

const findListFromArgs = (state, listArgs) => {
  /**
   * Helper method to find appropriate list from listArgs.
   *
   * Because we nest studyUserLists to arbitrary locations/depths,
   * finding the correct list becomes a little bit harder
   */
  // let list = Object.assign({}, state.studyUser.lists, {});
  let list = { ...state.studyUser.lists }
  for(let i = 0; i < listArgs.length; i++) {
    list = list[listArgs[i]];
    if(!list) {
      return false;
    }
  }
  return list;
}

const shouldFetchList = (state, listArgs) => {
  /**
   * Helper method to determine whether to fetch the list or not from arbitrary
   * listArgs
   *
   * NOTE: Uncomment console logs to help debugging
   */
  // console.log("shouldFetchList with these args ", listArgs, "?");
  const list = findListFromArgs(state, listArgs);
  // console.log("LIST in question: ", list);
  if(!list || !list.items) {
    // yes, the list we're looking for wasn't found
    // console.log("X shouldFetch - true: list not found");
    return true;
  } else if(list.isFetching) {
    // no, this list is already fetching
    // console.log("X shouldFetch - false: fetching");
    return false
  } else if(new Date().getTime() - list.lastUpdated > (1000 * 60 * 5)) {
    // yes, it's been longer than 5 minutes since the last fetch
    // console.log("X shouldFetch - true: older than 5 minutes");
    return true;
  } else {
    // maybe, depends on if the list was invalidated
    // console.log("X shouldFetch - " + list.didInvalidate + ": didInvalidate");
    return list.didInvalidate;
  }
}

export const fetchListIfNeeded = (...listArgs) => (dispatch, getState) => {
  if(listArgs.length === 0) {
    // If no arguments passed, make the list we want "all"
    listArgs = ["all"];
  }
  // // so the new api works without changing the listArgs in all the components
  // listArgs = listArgs.map(arg => arg.toString().replaceAll('study', 'group'));
  if(shouldFetchList(getState(), listArgs)) {
    return dispatch(fetchList(...listArgs));
  } else {
    return dispatch(returnStudyUserListPromise(...listArgs));
  }
}

export const returnStudyUserListPromise = (...listArgs) => (dispatch, getState) => {
  /**
   * This returns the list object from the reducer so that we can do things with it in
   * the component.
   *
   * For the "fetchIfNeeded()" functionality, we need to return a promised object
   * EVEN IF we don't need to fetch it. This is because if we have any .then()'s
   * in the components, they will fail when we don't need to fetch.
   */

  // return the array of objects just like the regular fetch
  const state = getState();
  const listItemIds = findListFromArgs(state, listArgs).items
  const listItems = listItemIds.map(id => state.studyUser.byId[id]);

  return new Promise((resolve, reject) => {
    resolve({
      type: "RETURN_STUDY_USER_LIST_WITHOUT_FETCHING"
      , listArgs: listArgs
      , list: listItems
      , success: true
    })
  });
}

export const REQUEST_STUDY_USER_LIST = "REQUEST_STUDY_USER_LIST"
function requestStudyUserList(listArgs) {
  return {
    type: REQUEST_STUDY_USER_LIST
    , listArgs
  }
}

export const RECEIVE_STUDY_USER_LIST = "RECEIVE_STUDY_USER_LIST"
function receiveStudyUserList(json, listArgs) {
  return {
    type: RECEIVE_STUDY_USER_LIST
    , listArgs
    , list: json.groupUsers
    , nodes: json.nodes ? json.nodes : null
    , questions: json.questions ? json.questions : null
    , completionMap: json.studyUserCompletedMap ? json.studyUserCompletedMap : null
    , success: json.success
    , error: json.message
    , receivedAt: Date.now()
  }
}

export const ADD_STUDY_USER_TO_LIST = "ADD_STUDY_USER_TO_LIST";
export function addStudyUserToList(id, ...listArgs) {
  // console.log("Add studyUser to list", id);
  if(listArgs.length === 0) {
    listArgs = ["all"];
  }
  return {
    type: ADD_STUDY_USER_TO_LIST
    , id
    , listArgs
  }
}

export const REMOVE_STUDY_USER_FROM_LIST = "REMOVE_STUDY_USER_FROM_LIST"
export function removeStudyUserFromList(id, ...listArgs) {
  if(listArgs.length === 0) {
    listArgs = ['all'];
  }
  return {
    type: REMOVE_STUDY_USER_FROM_LIST
    , id
    , listArgs
  }
}

export function fetchList(...listArgs) {
  return dispatch => {
    if(listArgs.length === 0) {
      // default to "all" list if we don't pass any listArgs
      listArgs = ["all"];
    }
    // // so the new api works without changing the listArgs in all the components
    // listArgs = listArgs.map(arg => arg.toString().replaceAll('study', 'group'));
    dispatch(requestStudyUserList(listArgs))
    /**
     * determine what api route we want to hit
     *
     * NOTE: use listArgs to determine what api call to make.
     * if listArgs[0] == null or "all", return list
     *
     * if listArgs has 1 arg, return "/api/group-users/by-[ARG]"
     *
     * if 2 args, additional checks required.
     *  if 2nd arg is a string, return "/api/group-users/by-[ARG1]/[ARG2]".
     *    ex: /api/group-users/by-category/:category
     *  if 2nd arg is an array, though, return "/api/group-users/by-[ARG1]-list" with additional query string
     *
     * TODO:  make this accept arbitrary number of args. Right now if more
     * than 2, it requires custom checks on server
     */
    let apiTarget = "/api/group-users";
    if(listArgs.length == 1 && listArgs[0] !== "all") {
      apiTarget += `/by-${listArgs[0]}`;
    } else if(listArgs.length == 2 && Array.isArray(listArgs[1])) {
      // length == 2 has it's own check, specifically if the second param is an array
      // if so, then we need to call the "listByValues" api method instead of the regular "listByRef" call
      // this can be used for querying for a list of studyUsers given an array of studyUser id's, among other things
      apiTarget += `/by-${listArgs[0]}-list?`;
      // build query string
      for(let i = 0; i < listArgs[1].length; i++) {
        apiTarget += `${listArgs[0]}=${listArgs[1][i]}&`
      }
    } else if(listArgs.length == 2) {
      // ex: ("author","12345")
      apiTarget += `/by-${listArgs[0]}/${listArgs[1]}`;
    } else if(listArgs.length > 2) {
      apiTarget += `/by-${listArgs[0]}/${listArgs[1]}`;
      for(let i = 2; i < listArgs.length; i++) {
        apiTarget += `/${listArgs[i]}`;
      }
    }
    return callAPI(apiTarget).then(
      json => dispatch(receiveStudyUserList(json, listArgs))
    )
  }
}

/**
 * LIST UTIL METHODS
 */
export const SET_STUDY_USER_FILTER = "SET_STUDY_USER_FILTER"
export function setFilter(filter, ...listArgs) {
  if(listArgs.length === 0) {
    listArgs = ["all"];
  }
  // // so the new api works without changing the listArgs in all the components
  // listArgs = listArgs.map(arg => arg.toString().replaceAll('study', 'group'));
  return {
    type: SET_STUDY_USER_FILTER
    , filter
    , listArgs
  }
}

export const SET_STUDY_USER_PAGINATION = "SET_STUDY_USER_PAGINATION"
export function setPagination(pagination, ...listArgs) {
  if(listArgs.length === 0) {
    listArgs = ["all"];
  }
  // // so the new api works without changing the listArgs in all the components
  // listArgs = listArgs.map(arg => arg.toString().replaceAll('study', 'group'));
  return {
    type: SET_STUDY_USER_PAGINATION
    , pagination
    , listArgs
  }
}

export const INVALIDATE_STUDY_USER_LIST = "INVALIDATE_STUDY_USER_LIST"
export function invalidateList(...listArgs) {
  if(listArgs.length === 0) {
    listArgs = ["all"];
  }
  // // so the new api works without changing the listArgs in all the components
  // listArgs = listArgs.map(arg => arg.toString().replaceAll('study', 'group'));
  return {
    type: INVALIDATE_STUDY_USER_LIST
    , listArgs
  }
}


export const REQUEST_NEW_STUDY_MATERIAL_ALERT = "REQUEST_NEW_STUDY_MATERIAL_ALERT";
function requestNewStudyMaterialAlert(studyId) {
  return {
    type: REQUEST_NEW_STUDY_MATERIAL_ALERT
    , id: studyId
  }
}

export const RECEIVE_NEW_STUDY_MATERIAL_ALERT = "RECEIVE_NEW_STUDY_MATERIAL_ALERT";
function receiveNewStudyMaterialAlert(json) {
  return {
    type: RECEIVE_NEW_STUDY_MATERIAL_ALERT
    , id: json.course ? json.course._id : null
    , study: json.group
    , studyUsers: json.groupUsers
    , message: json.message
    , success: json.success
    , error: json.message
    , receivedAt: Date.now()
  }
}

export function sendNewStudyMaterialAlert(studyId) {
  return dispatch => {
    dispatch(requestNewStudyMaterialAlert(studyId))
    return callAPI(`/api/group-users/new-material-alert/${studyId}`)
      .then(json => dispatch(receiveNewStudyMaterialAlert(json)))
  }
}

export const REQUEST_NEW_CUSTOM_STUDY_ALERT = "REQUEST_NEW_CUSTOM_STUDY_ALERT";
function requestNewCustomStudyAlert(studyId) {
  return {
    type: REQUEST_NEW_CUSTOM_STUDY_ALERT
    , id: studyId
  }
}

export const RECEIVE_NEW_CUSTOM_STUDY_ALERT = "RECEIVE_NEW_CUSTOM_STUDY_ALERT";
function receiveNewCustomStudyAlert(json) {
  return {
    type: RECEIVE_NEW_CUSTOM_STUDY_ALERT
    , id: json.course ? json.course._id : null
    , study: json.group
    , studyUsers: json.groupUsers
    , message: json.message
    , success: json.success
    , error: json.message
    , receivedAt: Date.now()
  }
}

export function sendNewCustomStudyAlert(studyId, data) {
  return dispatch => {
    dispatch(requestNewCustomStudyAlert(studyId))
    return callAPI(`/api/group-users/custom-study-alert/${studyId}`, 'POST', data)
      .then(json => dispatch(receiveNewCustomStudyAlert(json)))
  }
}

export const REQUEST_BULK_ONBOARDING_REMINDER = "REQUEST_BULK_ONBOARDING_REMINDER";
function requestBulkOnboardingReminder(studyId) {
  return {
    type: REQUEST_BULK_ONBOARDING_REMINDER
    , id: studyId
  }
}

export const RECEIVE_BULK_ONBOARDING_REMINDER = "RECEIVE_BULK_ONBOARDING_REMINDER";
function receiveBulkOnboardingReminder(json) {
  return {
    type: RECEIVE_BULK_ONBOARDING_REMINDER
    , id: json.course ? json.course._id : null
    , course: json.course
    , study: json.group
    , studyUsers: json.groupUsers
    , message: json.message
    , success: json.success
    , error: json.message
    , receivedAt: Date.now()
  }
}

export function sendBulkOnboardingReminder(studyId) {
  return dispatch => {
    dispatch(requestBulkOnboardingReminder(studyId))
    return callAPI(`/api/group-users/bulk-onboarding-reminder/${studyId}`)
      .then(json => dispatch(receiveBulkOnboardingReminder(json)))
  }
}


export const REQUEST_COMPLETION_REMINDER = "REQUEST_COMPLETION_REMINDER";
function requestCompletionReminder(studyUserId) {
  return {
    type: REQUEST_COMPLETION_REMINDER
    , id: studyUserId
  }
}

export const RECEIVE_COMPLETION_REMINDER = "RECEIVE_COMPLETION_REMINDER";
function receiveCompletionReminder(json) {
  return {
    type: RECEIVE_COMPLETION_REMINDER
    , message: json.message
    , success: json.success
    , error: json.message
    , receivedAt: Date.now()
  }
}

export function sendCompletionReminder(studyUserId) {
  return dispatch => {
    dispatch(requestCompletionReminder(studyUserId))
    return callAPI(`/api/group-users/send-completion-reminder/${studyUserId}`)
      .then(json => dispatch(receiveCompletionReminder(json)))
  }
}


export const REQUEST_BULK_COMPLETION_REMINDER = "REQUEST_BULK_COMPLETION_REMINDER";
function requestBulkCompletionReminder(studyId) {
  return {
    type: REQUEST_BULK_COMPLETION_REMINDER
    , id: studyId
  }
}

export const RECEIVE_BULK_COMPLETION_REMINDER = "RECEIVE_BULK_COMPLETION_REMINDER";
function receiveBulkCompletionReminder(json) {
  return {
    type: RECEIVE_BULK_COMPLETION_REMINDER
    , message: json.message
    , success: json.success
    , error: json.message
    , receivedAt: Date.now()
  }
}

export function sendBulkCompletionReminder(studyId) {
  return dispatch => {
    dispatch(requestBulkCompletionReminder(studyId))
    return callAPI(`/api/group-users/send-bulk-completion-reminder/${studyId}`)
      .then(json => dispatch(receiveBulkCompletionReminder(json)))
  }
}



// WEBPACK FOOTER //
// modules/studyUser/studyUserActions.js